/**
 * LaddersSection - Ladder Challenge display for Hub pages
 *
 * Clean ladder page following MEL design pattern:
 * - Hero with title left, info card right
 * - Simple tab navigation
 * - Clean leaderboard display
 *
 * Fetches real ladder data from the hub competitions endpoint,
 * then fetches standings and active challenges from the ladder API.
 */

import { ReactElement, useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { FaChartLine, FaTrophy, FaGamepad, FaUsers, FaCrosshairs, FaChevronUp, FaChevronDown, FaMinus } from "react-icons/fa";
import { useGetHubCompetitions } from "@/hooks/hub/useGetHubCompetitions";
import {LaddersApi, LadderChallengesApi} from "@devour/client";
import getConfig from "@/utils/getConfig";
import Skeleton from "react-loading-skeleton";
import "./LaddersSection.scss";

interface LaddersSectionProps {
    /** Hub slug for fetching competitions */
    surfaceSlug?: string;
    /** Competition umbrella ID (unused — data fetched via surfaceSlug) */
    competitionId?: string;
    /** Hub hero video URL (from heroSection) */
    heroVideoUrl?: string;
    /** Hub hero image URL (fallback if no video) */
    heroImageUrl?: string;
    /** Hub name for section title */
    hubName?: string;
}

/*
 * ============================================================================
 * API Response Interfaces
 * ============================================================================
 */

interface LadderParticipantResponse {
    id: string;
    ladderId: string;
    odId: string;
    displayName: string;
    rank: number;
    rating: number;
    wins: number;
    losses: number;
    draws: number;
    streak: number;
    gamesPlayed: number;
    isProvisional: boolean;
    status: string;
}

interface LadderChallengeResponse {
    id: string;
    ladderId: string;
    challengerOdId: string;
    challengerDisplayName: string;
    challengerRank: number;
    defenderOdId: string;
    defenderDisplayName: string;
    defenderRank: number;
    status: string;
    scheduledTime?: number;
    createdAt: number;
}

interface GetLadderStandingsResponse {
    standings: LadderParticipantResponse[];
    paginationInfo: {
        total: number;
        limit: number;
        offset: number;
    };
}

interface GetLadderChallengesResponse {
    challenges: LadderChallengeResponse[];
    paginationInfo: {
        total: number;
        limit: number;
        offset: number;
    };
}

/*
 * ============================================================================
 * API Fetch Functions
 * ============================================================================
 */

async function fetchLadderStandings(ladderId: string, limit = 20): Promise<GetLadderStandingsResponse> {
    const api = new LaddersApi(getConfig());
    return api.getLaddersIdStandings({id: ladderId, limit}) as unknown as Promise<GetLadderStandingsResponse>;
}

async function fetchLadderChallenges(ladderId: string, limit = 10): Promise<GetLadderChallengesResponse> {
    const api = new LadderChallengesApi(getConfig());
    // Fetch pending and accepted challenges (active ones)
    return api.getLaddersIdChallenges({id: ladderId, limit}) as unknown as Promise<GetLadderChallengesResponse>;
}

/*
 * ============================================================================
 * Demo Data (fallback when no real ladder is linked)
 * ============================================================================
 */

// Demo ladder data
const DEMO_LADDER = {
    name: "MONSTER ENERGY\nCOD LADDER CHALLENGE",
    description: "Climb the ranks in Call of Duty",
    game: "Call of Duty",
    mode: "1v1 Gunfight",
    rankingSystem: "Elo Rating",
    participants: 156,
    status: "ACTIVE",
    activeChallenges: 12,
};

// Demo standings
const DEMO_STANDINGS = [
    { rank: 1, name: "AceKiller", rating: 2450, wins: 45, losses: 8, streak: 7 },
    { rank: 2, name: "HeadshotHero", rating: 2380, wins: 52, losses: 15, streak: 4 },
    { rank: 3, name: "MonsterMash", rating: 2315, wins: 38, losses: 12, streak: -2 },
    { rank: 4, name: "FlickMaster", rating: 2280, wins: 41, losses: 18, streak: 3 },
    { rank: 5, name: "OneDeag", rating: 2245, wins: 35, losses: 14, streak: 0 },
    { rank: 6, name: "SprayControl", rating: 2210, wins: 33, losses: 16, streak: 2 },
    { rank: 7, name: "PeekAboo", rating: 2175, wins: 29, losses: 15, streak: -1 },
    { rank: 8, name: "SmokeShow", rating: 2140, wins: 31, losses: 19, streak: 5 },
    { rank: 9, name: "FlashBang", rating: 2105, wins: 27, losses: 17, streak: -3 },
    { rank: 10, name: "FragHunter", rating: 2070, wins: 24, losses: 15, streak: 1 },
    { rank: 11, name: "NadeKing", rating: 2035, wins: 22, losses: 14, streak: 2 },
    { rank: 12, name: "WallBanger", rating: 2000, wins: 20, losses: 13, streak: -1 },
    { rank: 13, name: "RecoilMaster", rating: 1965, wins: 19, losses: 14, streak: 0 },
    { rank: 14, name: "ClutchKing", rating: 1930, wins: 21, losses: 18, streak: 3 },
    { rank: 15, name: "DropShot", rating: 1895, wins: 18, losses: 15, streak: -2 },
    { rank: 16, name: "BunnyHopper", rating: 1860, wins: 17, losses: 16, streak: 1 },
    { rank: 17, name: "AimAssist", rating: 1825, wins: 16, losses: 15, streak: 0 },
    { rank: 18, name: "CampKing", rating: 1790, wins: 15, losses: 14, streak: -1 },
    { rank: 19, name: "QuickScope", rating: 1755, wins: 14, losses: 13, streak: 2 },
    { rank: 20, name: "NoobSlayer", rating: 1720, wins: 13, losses: 12, streak: 0 },
];

// Demo active challenges
const DEMO_CHALLENGES = [
    { id: 1, challenger: "HeadshotHero", challengerRank: 2, defender: "AceKiller", defenderRank: 1, status: "PENDING" },
    { id: 2, challenger: "FlickMaster", challengerRank: 4, defender: "MonsterMash", defenderRank: 3, status: "ACCEPTED" },
    { id: 3, challenger: "SprayControl", challengerRank: 6, defender: "OneDeag", defenderRank: 5, status: "PENDING" },
];

export const LaddersSection = ({
    surfaceSlug,
    competitionId: _competitionId,
    heroVideoUrl: _heroVideoUrl,
    heroImageUrl: _heroImageUrl,
    hubName: _hubName = "Ladder Challenge",
}: LaddersSectionProps): ReactElement => {
    const [activeTab, setActiveTab] = useState<"standings" | "challenges" | "rules">("standings");

    // Fetch ladders for this hub
    const { data: competitionsData, isLoading: isLoadingLadders } = useGetHubCompetitions(surfaceSlug, {
        type: "LADDER",
    });

    // Get the first (featured) ladder, or use demo data as fallback
    const ladders = competitionsData?.competitions || [];
    const featuredLadder = ladders[0];
    const ladderId = featuredLadder?.id;

    // Fetch real standings when we have a ladder ID
    const { data: standingsData, isLoading: isLoadingStandings } = useQuery({
        queryKey: ["ladder-standings", ladderId],
        queryFn: () => fetchLadderStandings(ladderId!, 20),
        enabled: Boolean(ladderId),
        staleTime: 60 * 1000, // 1 minute
    });

    // Fetch real challenges when we have a ladder ID
    const { data: challengesData, isLoading: isLoadingChallenges } = useQuery({
        queryKey: ["ladder-challenges", ladderId],
        queryFn: () => fetchLadderChallenges(ladderId!, 10),
        enabled: Boolean(ladderId),
        staleTime: 60 * 1000, // 1 minute
    });

    // Use real standings or fall back to demo
    const standings = standingsData?.standings || [];
    const totalParticipants = standingsData?.paginationInfo?.total || 0;
    const useRealStandings = standings.length > 0;

    // Use real challenges or fall back to demo
    const challenges = challengesData?.challenges || [];
    const useRealChallenges = challenges.length > 0;

    // Derive display values from real data or fallback to demo
    const displayData = useMemo(() => {
        if (!featuredLadder) {
            return DEMO_LADDER;
        }

        return {
            name: featuredLadder.name || "LADDER CHALLENGE",
            description: featuredLadder.description || "Climb the ranks",
            game: featuredLadder.game?.name || "Game TBD",
            mode: `${featuredLadder.teamSize || 1}v${featuredLadder.teamSize || 1}`,
            rankingSystem: "Elo Rating",
            participants: totalParticipants || featuredLadder.participantCount || 0,
            status: featuredLadder.status || "ACTIVE",
            activeChallenges: challenges.filter(c => c.status === "PENDING" || c.status === "ACCEPTED").length,
        };
    }, [featuredLadder, totalParticipants, challenges]);

    const isLoading = isLoadingLadders;

    // Loading state
    if (isLoading) {
        return (
            <section className="ladders-section">
                <div className="ladders-section__info-bar">
                    <Skeleton width={120} height={20} />
                    <Skeleton width={120} height={20} />
                    <Skeleton width={120} height={20} />
                    <Skeleton width={120} height={20} />
                </div>
                <div className="ladders-section__tabs">
                    <Skeleton width={400} height={40} />
                </div>
            </section>
        );
    }

    return (
        <section className="ladders-section">
            {/* Ladder Info Bar */}
            <div className="ladders-section__info-bar">
                <div className="ladders-section__info-item">
                    <FaGamepad size={14} />
                    <span>{displayData.game}</span>
                </div>
                <div className="ladders-section__info-item">
                    <FaCrosshairs size={14} />
                    <span>{displayData.mode}</span>
                </div>
                <div className="ladders-section__info-item">
                    <FaTrophy size={14} />
                    <span>{displayData.rankingSystem}</span>
                </div>
                <div className="ladders-section__info-item">
                    <FaUsers size={14} />
                    <span>{displayData.participants} Players</span>
                </div>
                <div className="ladders-section__info-actions">
                    <button className="ladders-section__btn ladders-section__btn--primary">
                        JOIN LADDER
                    </button>
                </div>
            </div>

            {/* Tab Navigation */}
            <div className="ladders-section__tabs">
                <button
                    className={`ladders-section__tab ${activeTab === "standings" ? "ladders-section__tab--active" : ""}`}
                    onClick={() => setActiveTab("standings")}
                >
                    <FaChartLine size={14} /> LEADERBOARD
                </button>
                <button
                    className={`ladders-section__tab ${activeTab === "challenges" ? "ladders-section__tab--active" : ""}`}
                    onClick={() => setActiveTab("challenges")}
                >
                    CHALLENGES ({displayData.activeChallenges})
                </button>
                <button
                    className={`ladders-section__tab ${activeTab === "rules" ? "ladders-section__tab--active" : ""}`}
                    onClick={() => setActiveTab("rules")}
                >
                    HOW IT WORKS
                </button>
            </div>

            {/* Standings Tab */}
            {activeTab === "standings" &&
                <div className="ladders-section__standings-section">
                    {/* How It Works - Above Standings */}
                    <div className="ladders-section__how-it-works">
                        <h3>How It Works</h3>
                        <div className="ladders-section__how-it-works-grid">
                            <div className="ladders-section__how-it-works-item">
                                <span className="ladders-section__how-it-works-num">1</span>
                                <span>Join the ladder and get placed</span>
                            </div>
                            <div className="ladders-section__how-it-works-item">
                                <span className="ladders-section__how-it-works-num">2</span>
                                <span>Challenge players up to 5 ranks above</span>
                            </div>
                            <div className="ladders-section__how-it-works-item">
                                <span className="ladders-section__how-it-works-num">3</span>
                                <span>Win matches to climb the leaderboard</span>
                            </div>
                            <div className="ladders-section__how-it-works-item">
                                <span className="ladders-section__how-it-works-num">4</span>
                                <span>Defend your spot against challengers</span>
                            </div>
                        </div>
                    </div>

                    <div className="ladders-section__standings-header">
                        <h2 className="ladders-section__standings-title">LADDER STANDINGS</h2>
                        <p className="ladders-section__standings-subtitle">
                            {displayData.participants} participants • {displayData.rankingSystem}
                        </p>
                    </div>

                    {isLoadingStandings
                        ? <div className="ladders-section__standings-table">
                            {[...Array(5)].map((_, i) =>
                                <div key={i} className="ladders-section__standings-row">
                                    <Skeleton width="100%" height={40} />
                                </div>)}
                        </div>
                        : <div className="ladders-section__standings-table">
                            {/* Table Header */}
                            <div className="ladders-section__standings-row ladders-section__standings-row--header">
                                <span className="ladders-section__col ladders-section__col--rank">RANK</span>
                                <span className="ladders-section__col ladders-section__col--player">PLAYER</span>
                                <span className="ladders-section__col ladders-section__col--rating">RATING</span>
                                <span className="ladders-section__col ladders-section__col--record">RECORD</span>
                                <span className="ladders-section__col ladders-section__col--streak">STREAK</span>
                                <span className="ladders-section__col ladders-section__col--action"></span>
                            </div>

                            {/* Table Rows - Real data or Demo fallback */}
                            {(useRealStandings ? standings : DEMO_STANDINGS).map((player) => {
                                const rank = player.rank;
                                const name = "displayName" in player ? player.displayName : player.name;
                                const wins = player.wins;
                                const losses = player.losses;
                                const rating = player.rating;
                                const streak = player.streak;

                                return (
                                    <div
                                        key={rank}
                                        className={`ladders-section__standings-row ${rank <= 3 ? "ladders-section__standings-row--top3" : ""}`}
                                    >
                                        <span className="ladders-section__col ladders-section__col--rank">
                                            {rank <= 3 && <FaTrophy size={12} />}
                                            {rank}
                                        </span>
                                        <span className="ladders-section__col ladders-section__col--player">
                                            {name}
                                        </span>
                                        <span className="ladders-section__col ladders-section__col--rating">
                                            {rating}
                                        </span>
                                        <span className="ladders-section__col ladders-section__col--record">
                                            <span className="ladders-section__wins">{wins}</span>
                                            <span className="ladders-section__sep">-</span>
                                            <span className="ladders-section__losses">{losses}</span>
                                        </span>
                                        <span className="ladders-section__col ladders-section__col--streak">
                                            {streak === 0
                                                ? <span className="ladders-section__streak ladders-section__streak--neutral">
                                                    <FaMinus size={10} />
                                                </span>
                                                : streak > 0
                                                    ? <span className="ladders-section__streak ladders-section__streak--win">
                                                        <FaChevronUp size={10} /> W{streak}
                                                    </span>
                                                    : <span className="ladders-section__streak ladders-section__streak--loss">
                                                        <FaChevronDown size={10} /> L{Math.abs(streak)}
                                                    </span>
                                            }
                                        </span>
                                        <span className="ladders-section__col ladders-section__col--action">
                                            {rank > 1 && rank <= 6 &&
                                                <button className="ladders-section__challenge-btn">
                                                    Challenge
                                                </button>
                                            }
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    }

                    {displayData.participants > 20 &&
                        <p className="ladders-section__standings-more">
                            + {displayData.participants - 20} more participants
                        </p>
                    }
                </div>
            }

            {/* Challenges Tab */}
            {activeTab === "challenges" &&
                <div className="ladders-section__challenges-section">
                    <h2>Active Challenges</h2>
                    {isLoadingChallenges
                        ? <div className="ladders-section__challenges-list">
                            {[...Array(3)].map((_, i) =>
                                <div key={i} className="ladders-section__challenge-card">
                                    <Skeleton width="100%" height={60} />
                                </div>)}
                        </div>
                        : <div className="ladders-section__challenges-list">
                            {(useRealChallenges ? challenges : DEMO_CHALLENGES).map((challenge) => {
                                // Handle both real API response and demo data shapes
                                const id = challenge.id;
                                const challengerRank = challenge.challengerRank;
                                const challengerName = "challengerDisplayName" in challenge
                                    ? challenge.challengerDisplayName
                                    : (challenge as typeof DEMO_CHALLENGES[number]).challenger;
                                const defenderRank = challenge.defenderRank;
                                const defenderName = "defenderDisplayName" in challenge
                                    ? challenge.defenderDisplayName
                                    : (challenge as typeof DEMO_CHALLENGES[number]).defender;
                                const status = challenge.status;

                                return (
                                    <div key={id} className="ladders-section__challenge-card">
                                        <div className="ladders-section__challenge-challenger">
                                            <span className="ladders-section__challenge-rank">#{challengerRank}</span>
                                            <span className="ladders-section__challenge-name">{challengerName}</span>
                                        </div>
                                        <span className="ladders-section__challenge-vs">VS</span>
                                        <div className="ladders-section__challenge-defender">
                                            <span className="ladders-section__challenge-rank">#{defenderRank}</span>
                                            <span className="ladders-section__challenge-name">{defenderName}</span>
                                        </div>
                                        <span className={`ladders-section__challenge-status ladders-section__challenge-status--${status.toLowerCase()}`}>
                                            {status}
                                        </span>
                                    </div>
                                );
                            })}
                            {(useRealChallenges ? challenges : DEMO_CHALLENGES).length === 0 &&
                                <p className="ladders-section__no-challenges">No active challenges at the moment.</p>
                            }
                        </div>
                    }
                </div>
            }

            {/* Rules Tab */}
            {activeTab === "rules" &&
                <div className="ladders-section__rules-section">
                    <h2>How It Works</h2>
                    <div className="ladders-section__rules-grid">
                        <div className="ladders-section__rule-card">
                            <div className="ladders-section__rule-number">1</div>
                            <div className="ladders-section__rule-content">
                                <h3>Join the Ladder</h3>
                                <p>Create your profile and get placed at the bottom of the standings.</p>
                            </div>
                        </div>
                        <div className="ladders-section__rule-card">
                            <div className="ladders-section__rule-number">2</div>
                            <div className="ladders-section__rule-content">
                                <h3>Challenge Higher Ranks</h3>
                                <p>Challenge players up to 5 ranks above you to take their spot.</p>
                            </div>
                        </div>
                        <div className="ladders-section__rule-card">
                            <div className="ladders-section__rule-number">3</div>
                            <div className="ladders-section__rule-content">
                                <h3>Win & Climb</h3>
                                <p>Win your match to gain Elo rating and climb the leaderboard.</p>
                            </div>
                        </div>
                        <div className="ladders-section__rule-card">
                            <div className="ladders-section__rule-number">4</div>
                            <div className="ladders-section__rule-content">
                                <h3>Defend Your Spot</h3>
                                <p>Accept challenges from below to maintain your ranking.</p>
                            </div>
                        </div>
                    </div>
                </div>
            }
        </section>
    );
};
